import { createContext, useContext, useState, useCallback, useRef } from 'react'
import { CheckCircle, AlertTriangle, Info, X } from 'lucide-react'

type ToastType = 'success' | 'error' | 'info'

interface Toast {
  id: string
  message: string
  type: ToastType
}

interface ToastCtx {
  toast: (message: string, type?: ToastType) => void
}

const ToastContext = createContext<ToastCtx>({ toast: () => {} })

export function useToast() {
  return useContext(ToastContext)
}

const ICONS = {
  success: CheckCircle,
  error:   AlertTriangle,
  info:    Info,
}

const COLORS: Record<ToastType, { bg: string; border: string; color: string; icon: string }> = {
  success: { bg: 'rgba(50,200,100,0.12)', border: 'rgba(50,200,100,0.35)', color: '#32c864', icon: '#32c864' },
  error:   { bg: 'rgba(204,51,0,0.12)',   border: 'rgba(204,51,0,0.35)',   color: '#cc3300', icon: '#cc3300' },
  info:    { bg: 'rgba(255,198,0,0.1)',   border: 'rgba(255,198,0,0.3)',   color: '#a0a0a0', icon: '#ffc600' },
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
    const t = timers.current.get(id)
    if (t) { clearTimeout(t); timers.current.delete(id) }
  }, [])

  const toast = useCallback((message: string, type: ToastType = 'success') => {
    const id = Date.now().toString()
    setToasts(prev => [...prev.slice(-3), { id, message, type }]) // max 4 at once
    const timer = setTimeout(() => dismiss(id), 3500)
    timers.current.set(id, timer)
  }, [dismiss])

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {/* Toast container */}
      <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-2 pointer-events-none"
        style={{ maxWidth: 340 }}>
        {toasts.map(t => {
          const c = COLORS[t.type]
          const Icon = ICONS[t.type]
          return (
            <div key={t.id}
              className="flex items-center gap-3 px-4 py-3 rounded-xl pointer-events-auto"
              style={{
                background: c.bg,
                border: `1px solid ${c.border}`,
                color: c.color,
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                animation: 'slideInRight 0.25s ease',
              }}>
              <Icon size={16} style={{ color: c.icon, flexShrink: 0 }} />
              <p className="text-sm font-medium flex-1" style={{ color: '#fff' }}>{t.message}</p>
              <button onClick={() => dismiss(t.id)} style={{ color: '#555', flexShrink: 0 }}>
                <X size={14} />
              </button>
            </div>
          )
        })}
      </div>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </ToastContext.Provider>
  )
}
